import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { api, errMsg } from "@/lib/galleryApi";

export const formatBytes = (b) => (b >= 1e9 ? `${(b / 1e9).toFixed(2)} Go` : b >= 1e6 ? `${(b / 1e6).toFixed(1)} Mo` : `${Math.round((b || 0) / 1e3)} Ko`);

// Polls a background purge job until it finishes
export function usePurgeJob(onDone) {
  const [job, setJob] = useState(null);
  const timer = useRef(null);
  const track = (j) => {
    setJob(j);
    clearInterval(timer.current);
    timer.current = setInterval(async () => {
      const cur = await api.get(`/galleries/jobs/${j.id || j.job_id}`).catch(() => null);
      if (!cur) return;
      setJob(cur);
      if (cur.status !== "running") {
        clearInterval(timer.current);
        onDone?.(cur);
      }
    }, 1500);
  };
  useEffect(() => () => clearInterval(timer.current), []);
  return { job, track, clear: () => setJob(null) };
}

export function PurgeDialog({ ids, onClose, galleryId, onStarted }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => setText(""), [ids]);
  const go = async () => {
    setBusy(true);
    try {
      const job = await api.post(`/galleries/${galleryId}/photos/purge`, { photo_ids: ids, confirm: "SUPPRIMER" });
      onStarted(job, ids);
      onClose();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };
  return (
    <AlertDialog open={!!ids} onOpenChange={(o) => !o && onClose()}>
      <AlertDialogContent className="bg-[#0B1120] border-red-500/40 text-white" data-testid="purge-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>Supprimer definitivement {ids?.length} photo(s) ?</AlertDialogTitle>
          <AlertDialogDescription className="text-[#94A3B8]">
            L'original, la miniature, l'apercu et les versions retouchees seront effaces du stockage. Les fichiers encore utilises ailleurs sont conserves. <b className="text-red-400">Action irreversible.</b>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <div>
          <label className="text-xs text-[#94A3B8]">Tapez <b className="text-white">SUPPRIMER</b> pour confirmer</label>
          <Input value={text} onChange={(e) => setText(e.target.value)} className="mt-1.5 bg-[#060B14] border-[#1A2332] text-white" data-testid="purge-confirm-input" />
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel className="bg-transparent border-[#1A2332] text-[#94A3B8]">Annuler</AlertDialogCancel>
          <Button onClick={go} disabled={text !== "SUPPRIMER" || busy} className="bg-red-600 hover:bg-red-700 text-white" data-testid="purge-confirm-btn">Supprimer definitivement</Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export function PurgeProgress({ job, onClose }) {
  if (!job) return null;
  const pct = job.total ? Math.round((job.done / job.total) * 100) : 100;
  return (
    <div className="mb-4 bg-[#0B1120] border border-red-500/30 rounded-xl p-4" data-testid="purge-progress">
      <div className="flex items-center gap-3">
        <Progress value={pct} className="h-2 flex-1 bg-[#1A2332]" />
        <span className="text-xs font-mono text-red-300 w-24 text-right">{job.done}/{job.total}</span>
        {job.status !== "running" && <button onClick={onClose} className="text-xs text-[#64748B] hover:text-white">Fermer</button>}
      </div>
      <p className="text-[11px] text-[#94A3B8] mt-2" data-testid="purge-status">
        {job.status === "running" ? "Suppression des fichiers en cours…" : job.status === "done" ? "Suppression terminee" : "Suppression partielle — relancez pour les photos restantes"}
        {" · "}{job.files_deleted} fichier(s) efface(s) (contenu detruit) · {formatBytes(job.bytes_freed)} liberes{job.files_kept_shared ? ` · ${job.files_kept_shared} fichier(s) partage(s) conserve(s)` : ""}{job.errors ? ` · ${job.errors} erreur(s)` : ""}
      </p>
    </div>
  );
}
